/* BP-009 — BRRRR Intelligence tab.

   Buy · Rehab · Rent · Refinance · Repeat.
   Layout:
     • Inputs grid   — acquisition + rehab + rental + refinance
     • Hero KPI      — Capital Left · BRRRR Score · Cash Recovered
     • Metrics grid  — CoC · DSCR · Monthly Cash Flow · Equity Created
     • Recovery bar  — visual capital-recovery gauge
     • Warnings pills
     • 5-year equity projection
     • AI panel (with strategy comparison)
     • Saved analyses
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, RotateCcw, Save, Sparkles, Download, RefreshCcw,
} from "lucide-react";

const INP = "w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";

const EMPTY_INPUTS = {
  purchase_price: "", arv: "", repair_budget: "0",
  acquisition_closing_costs: "", rehab_timeline_months: "",
  monthly_rent: "", vacancy_rate: "", monthly_operating_expenses: "",
  refinance_ltv: "", interest_rate: "", loan_term_years: "",
  refinance_closing_costs: "", target_cash_reserve: "",
};

export default function BRRRRTab({ property }) {
  const [inputs, setInputs] = useState({
    ...EMPTY_INPUTS,
    purchase_price: property.asking_price || "",
    arv: property.published_arv || property.estimated_arv || "",
    repair_budget: property.published_repair_estimate?.total || "0",
  });
  const [scenarioName, setScenarioName] = useState("Base BRRRR");
  const [current, setCurrent] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/properties/${property.id}/brrrr`);
      setAnalyses(data.items || []);
      if (data.items?.[0] && !current) {
        setCurrent(data.items[0]);
        setInputs({ ...EMPTY_INPUTS, ...data.items[0].inputs });
        setScenarioName(data.items[0].scenario_name);
      }
    } catch { toast.error("Failed to load BRRRR"); }
    finally { setLoading(false); }
  }, [property.id, current]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setInputs((s) => ({ ...s, [k]: e.target.value }));

  const calc = useCallback(async (save) => {
    if (!inputs.purchase_price || !inputs.arv || !inputs.monthly_rent) {
      toast.error("Purchase, ARV, and Monthly Rent are required.");
      return;
    }
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/brrrr/calculate`,
        { inputs, save, scenario_name: scenarioName },
      );
      if (save) {
        setCurrent(data);
        toast.success(`${data.scenario_name} saved`);
        load();
      } else {
        setCurrent({ inputs, outputs: data.outputs, scenario_name: scenarioName });
        toast.success("Calculated");
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Calculation failed"); }
    finally { setCalculating(false); }
  }, [inputs, property.id, scenarioName, load]);

  const runAi = useCallback(async () => {
    if (!current?.id) { toast.error("Save the analysis first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post(`/v3/brrrr/${current.id}/ai-review`);
      setCurrent(data);
      toast.success("AI review ready");
    } catch { toast.error("AI review failed"); }
    finally { setAiLoading(false); }
  }, [current]);

  const exportPdf = useCallback(() => {
    if (!current?.id) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v3/brrrr/${current.id}/export/pdf`, "_blank");
  }, [current]);

  const outs = useMemo(() => current?.outputs || {}, [current]);
  const recoveryRatioPct = outs.capital_recovery_ratio ? Math.min(100, parseFloat(outs.capital_recovery_ratio) * 100) : 0;
  const brrrrScore = outs.brrrr_score ? parseInt(String(outs.brrrr_score), 10) : null;
  const scoreBand = brrrrScore == null ? null
    : brrrrScore >= 85 ? { color: "text-emerald-700", dot: "bg-emerald-500", label: "ELITE" }
    : brrrrScore >= 70 ? { color: "text-lime-700",    dot: "bg-lime-500",    label: "STRONG" }
    : brrrrScore >= 55 ? { color: "text-amber-700",   dot: "bg-amber-500",   label: "GOOD" }
    :                      { color: "text-red-700",    dot: "bg-red-500",     label: "REVIEW" };

  if (loading) return (
    <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="brrrr-loading">
      <Loader2 size={14} className="animate-spin"/> Loading…
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-panel-brrrr">
      {/* Inputs */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-3">
          <RefreshCcw size={12}/> Buy · Rehab · Rent · Refinance · Repeat
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <F label="Purchase Price *"><input value={inputs.purchase_price} onChange={set("purchase_price")} data-testid="br-purchase" className={INP} type="number"/></F>
          <F label="ARV *"><input value={inputs.arv} onChange={set("arv")} data-testid="br-arv" className={INP} type="number"/></F>
          <F label="Repair Budget"><input value={inputs.repair_budget} onChange={set("repair_budget")} className={INP} type="number"/></F>
          <F label="Acquisition Closing (blank → 2%)"><input value={inputs.acquisition_closing_costs} onChange={set("acquisition_closing_costs")} className={INP} type="number" placeholder="auto"/></F>
          <F label="Rehab Timeline (mo)"><input value={inputs.rehab_timeline_months} onChange={set("rehab_timeline_months")} className={INP} type="number" placeholder="3"/></F>
          <F label="Scenario Name"><input value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} data-testid="br-name" className={INP}/></F>

          <F label="Monthly Rent *"><input value={inputs.monthly_rent} onChange={set("monthly_rent")} data-testid="br-rent" className={INP} type="number"/></F>
          <F label="Vacancy Rate (0.05 = 5%)"><input value={inputs.vacancy_rate} onChange={set("vacancy_rate")} className={INP} type="number" step="0.01" placeholder="0.05"/></F>
          <F label="Operating Ex ($/mo · blank → 40% rent)"><input value={inputs.monthly_operating_expenses} onChange={set("monthly_operating_expenses")} className={INP} type="number" placeholder="auto"/></F>

          <F label="Refinance LTV (0.75 = 75%)"><input value={inputs.refinance_ltv} onChange={set("refinance_ltv")} className={INP} type="number" step="0.01" placeholder="0.75"/></F>
          <F label="Interest Rate (0.075 = 7.5%)"><input value={inputs.interest_rate} onChange={set("interest_rate")} className={INP} type="number" step="0.001" placeholder="0.075"/></F>
          <F label="Loan Term (yrs)"><input value={inputs.loan_term_years} onChange={set("loan_term_years")} className={INP} type="number" placeholder="30"/></F>
          <F label="Refinance Closing (blank → 2.5% of refi)"><input value={inputs.refinance_closing_costs} onChange={set("refinance_closing_costs")} className={INP} type="number" placeholder="auto"/></F>
          <F label="Target Cash Reserve"><input value={inputs.target_cash_reserve} onChange={set("target_cash_reserve")} className={INP} type="number" placeholder="5000"/></F>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => calc(false)} disabled={calculating} data-testid="br-calc"
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {calculating ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>}
            Calculate
          </button>
          <button onClick={() => calc(true)} disabled={calculating} data-testid="br-save"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-500 disabled:opacity-50">
            <Save size={12}/> Save
          </button>
          <button onClick={runAi} disabled={aiLoading || !current?.id} data-testid="br-ai"
                  className="inline-flex items-center gap-1 rounded-md border border-purple-300 bg-purple-50 text-purple-800 text-xs font-semibold px-3 py-1.5 hover:border-purple-500 disabled:opacity-40">
            {aiLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
            AI Review
          </button>
          <button onClick={exportPdf} disabled={!current?.id} data-testid="br-pdf"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-40">
            <Download size={12}/> PDF
          </button>
        </div>
      </div>

      {/* Hero KPI */}
      {current && (
        <>
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="br-results">
            <div className="flex flex-wrap items-start justify-between gap-5">
              <div className="min-w-[200px]">
                <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Capital Left in Deal</div>
                <div className={`text-4xl font-bold tabular-nums mt-1 ${parseFloat(outs.capital_left_in_deal) <= 0 ? "text-emerald-700" : "text-slate-900"}`}
                     data-testid="br-capital-left">
                  ${fmt(outs.capital_left_in_deal)}
                </div>
                <div className="text-xs text-slate-700 mt-1">
                  Cash Recovered <b>${fmt(outs.cash_recovered_at_refinance)}</b> of <b>${fmt(outs.total_project_cost)}</b> project cost
                </div>
                <div className="mt-2">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Capital Recovery</div>
                  <div className="h-2 bg-white rounded-full overflow-hidden border border-amber-200">
                    <div className={`h-full rounded-full ${recoveryRatioPct >= 90 ? "bg-emerald-500" : recoveryRatioPct >= 70 ? "bg-amber-500" : "bg-red-500"}`}
                         style={{ width: `${recoveryRatioPct}%` }}
                         data-testid="br-recovery-bar"/>
                  </div>
                  <div className="text-[10px] mt-0.5 text-slate-600 tabular-nums">{pct(outs.capital_recovery_ratio)}</div>
                </div>
              </div>

              <div className="flex flex-col items-center min-w-[120px]">
                <div className="text-[10px] uppercase tracking-wider text-amber-800/80">BRRRR Score</div>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className={`text-5xl font-bold tabular-nums ${scoreBand?.color || "text-slate-400"}`}
                        data-testid="br-score">{brrrrScore ?? "—"}</span>
                  <span className="text-sm text-slate-500 font-medium">/100</span>
                </div>
                {scoreBand && (
                  <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${scoreBand.color}`}>
                    <span className={`w-2 h-2 rounded-full ${scoreBand.dot}`}/> {scoreBand.label}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Secondary metrics */}
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
            <Metric label="Monthly Cash Flow" value={`$${fmt(outs.monthly_cash_flow)}`} testid="br-monthly-cf"/>
            <Metric label="Cash-on-Cash" value={pct(outs.cash_on_cash)} testid="br-coc"/>
            <Metric label="DSCR" value={ratio(outs.dscr)} testid="br-dscr"/>
            <Metric label="Equity Created" value={`$${fmt(outs.equity_created)}`}/>
            <Metric label="Refi Loan" value={`$${fmt(outs.refinance_loan_amount)}`}/>
            <Metric label="Monthly Mortgage" value={`$${fmt(outs.monthly_mortgage_payment)}`}/>
            <Metric label="NOI (annual)" value={`$${fmt(outs.noi)}`}/>
            <Metric label="Appraisal Margin" value={pct(outs.appraisal_margin)}/>
          </div>

          {/* Warnings */}
          {(outs.warnings || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-1" data-testid="br-warnings">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Warnings</div>
              {outs.warnings.map((w, i) => (
                <div key={i} className="text-[11px] text-slate-800">
                  <span className={`text-[9px] px-1 py-0.5 rounded uppercase font-semibold mr-2 ${
                    w.severity === "critical" ? "bg-red-100 text-red-700"
                    : w.severity === "high"   ? "bg-orange-100 text-orange-700"
                    :                            "bg-amber-100 text-amber-700"
                  }`}>{w.severity}</span>
                  {w.message}
                </div>
              ))}
            </div>
          )}

          {/* 5-year equity projection */}
          {(outs.equity_projection || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="br-projection">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">5-Year Equity Projection</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Year</th>
                      <th className="text-right px-3 py-2 font-medium">Appreciation Equity</th>
                      <th className="text-right px-3 py-2 font-medium">Principal Paid</th>
                      <th className="text-right px-3 py-2 font-medium">Total Equity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outs.equity_projection.map((row) => {
                      const maxTotal = Math.max(...outs.equity_projection.map((r) => parseFloat(r.total_equity) || 0));
                      const shareN = maxTotal > 0 ? (parseFloat(row.total_equity) / maxTotal) * 100 : 0;
                      return (
                        <tr key={row.year} className="border-t border-slate-100">
                          <td className="px-3 py-1.5 text-slate-800">Y{row.year}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">${fmt(row.appreciation_equity)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">${fmt(row.principal_paid)}</td>
                          <td className="px-3 py-1.5 text-right">
                            <div className="tabular-nums font-semibold text-slate-900">${fmt(row.total_equity)}</div>
                            <div className="h-1 mt-0.5 bg-slate-100 rounded overflow-hidden">
                              <div className="h-full bg-amber-400 rounded" style={{ width: `${shareN}%` }}/>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* AI review */}
          {current.ai_review && (
            <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 space-y-1" data-testid="br-ai-block">
              <div className="text-[10px] uppercase tracking-wider text-purple-700">AI Review</div>
              <div className="text-sm text-slate-900">{current.ai_review.summary}</div>
              {current.ai_review.recoveryProbability && (
                <div className="text-[11px] text-slate-700"><b>Recovery probability:</b> {current.ai_review.recoveryProbability}</div>
              )}
              {current.ai_review.optimalRefinanceTiming && (
                <div className="text-[11px] text-slate-700"><b>Optimal refinance timing:</b> {current.ai_review.optimalRefinanceTiming}</div>
              )}
              {current.ai_review.refinanceRisks?.length > 0 && (
                <div className="text-[11px] text-slate-700"><b>Refinance risks:</b> {current.ai_review.refinanceRisks.join("; ")}</div>
              )}
              {current.ai_review.strategyComparison?.recommended && (
                <div className="text-[11px] text-slate-700">
                  <b>Strategy comparison:</b> Recommended <b>{current.ai_review.strategyComparison.recommended}</b>. {current.ai_review.strategyComparison.reasoning}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* History */}
      {analyses.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="br-history">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Saved Analyses ({analyses.length})</div>
          <div className="space-y-1">
            {analyses.map((a) => (
              <button key={a.id}
                      onClick={() => { setCurrent(a); setInputs({ ...EMPTY_INPUTS, ...a.inputs }); }}
                      data-testid={`br-history-${a.id.slice(0,8)}`}
                      className="w-full text-left flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-slate-50">
                <span>v{a.version} · {a.scenario_name}</span>
                <span className="tabular-nums text-slate-600">
                  Score {a.outputs?.brrrr_score || "—"} · Capital Left ${fmt(a.outputs?.capital_left_in_deal)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);
const Metric = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-base font-semibold text-slate-900 tabular-nums mt-0.5" data-testid={testid}>{value}</div>
  </div>
);

function fmt(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function pct(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : `${(n * 100).toFixed(1)}%`;
}
function ratio(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : n.toFixed(2);
}
